/**
 * Ưu đãi chào khách mới (KH-GĐ2 mục 6, ADR-075): tính SỐ GIẢM và câu mô tả — dùng chung máy chủ, web quầy, Mini App
 * để số khách thấy (giá gạch) = số quầy thấy = số máy chủ thu.
 *
 * Tệp này vào gói Mini App (dựng es2015): KHÔNG literal số nguyên lớn (`0n`, `1000n`) và không import hàm có literal
 * đó — chỉ dùng `BigInt(...)`. Có test đọc nguồn chặn.
 */
import { formatVndChuoi } from './money.ts'

export type KieuGiamUuDai = 'phan_tram' | 'so_tien' | 'tang_dich_vu'

export interface DieuKhoanUuDai {
  kieu: KieuGiamUuDai
  /** Phần trăm theo basis point (2000 = 20%) — chỉ khi `phan_tram`. */
  giaTriBp: number | null
  /** Số tiền giảm — chỉ khi `so_tien`. */
  giaTriVnd: bigint | null
  /** Giảm tối đa (null = không giới hạn). */
  toiDaVnd: bigint | null
  /** Dịch vụ áp dụng (id service_variant). Rỗng = mọi dịch vụ. `tang_dich_vu` = đúng một. */
  dichVu: readonly string[]
}

export interface DongTinhUuDai {
  serviceVariantId: string | null
  priceVnd: bigint
  /** Dòng trả bằng tiền (dòng trừ buổi gói thì không giảm thêm). */
  traTien: boolean
}

const KHONG = BigInt(0)
const NGHIN = BigInt(1000)
const MUOI_NGHIN_BP = BigInt(10000)

/** Dòng nào được áp ưu đãi: trả tiền, là dịch vụ, nằm trong danh sách (rỗng = mọi dịch vụ). */
export function dongApDungUuDai(dk: DieuKhoanUuDai, dong: readonly DongTinhUuDai[]): boolean[] {
  return dong.map(
    (d) =>
      d.traTien &&
      d.serviceVariantId !== null &&
      d.priceVnd > KHONG &&
      (dk.dichVu.length === 0 || dk.dichVu.includes(d.serviceVariantId)),
  )
}

/**
 * Số giảm của ưu đãi trên các dòng áp dụng: luôn 0 ≤ giảm ≤ tổng dòng áp dụng, bội 1.000đ, ≤ giảm tối đa.
 *   phần trăm  → sàn 1.000đ của tổng × bp / 10000;
 *   số tiền    → min(số tiền, tổng);
 *   tặng dịch vụ → giá dòng áp dụng đầu tiên.
 */
export function tinhGiamUuDai(dk: DieuKhoanUuDai, dong: readonly DongTinhUuDai[]): bigint {
  const ap = dongApDungUuDai(dk, dong)
  let tong = KHONG
  let dau: bigint | null = null
  dong.forEach((d, i) => {
    if (!ap[i]) return
    tong += d.priceVnd
    if (dau === null) dau = d.priceVnd
  })
  if (tong === KHONG) return KHONG
  let giam = KHONG
  if (dk.kieu === 'phan_tram') {
    giam = ((tong * BigInt(dk.giaTriBp ?? 0)) / MUOI_NGHIN_BP / NGHIN) * NGHIN
  } else if (dk.kieu === 'so_tien') {
    const v = dk.giaTriVnd ?? KHONG
    giam = v < tong ? v : tong
  } else {
    giam = dau ?? KHONG
  }
  if (dk.toiDaVnd !== null && giam > dk.toiDaVnd) giam = dk.toiDaVnd
  // Giá dịch vụ luôn chẵn nghìn; cắt lần cuối cho chắc (không bao giờ lẻ, không âm, không vượt tổng).
  giam = (giam / NGHIN) * NGHIN
  if (giam < KHONG) return KHONG
  return giam > tong ? tong : giam
}

/** Câu mô tả ngắn, không ô chữ tự do: "Giảm 20% lần đầu, tối đa 100.000đ". */
export function moTaUuDai(dk: DieuKhoanUuDai, tenDichVu?: string): string {
  const toiDa = dk.toiDaVnd === null ? '' : `, tối đa ${formatVndChuoi(dk.toiDaVnd.toString())}`
  const dv = tenDichVu !== undefined && tenDichVu !== '' ? ` ${tenDichVu}` : ''
  if (dk.kieu === 'phan_tram') {
    const bp = dk.giaTriBp ?? 0
    // Phần trăm từ basis point bằng số nguyên: 2000 → "20", 1250 → "12,5".
    const le = bp % 100
    const pt =
      le === 0
        ? String((bp - le) / 100)
        : `${(bp - le) / 100},${String(le).padStart(2, '0').replace(/0$/, '')}`
    return `Giảm ${pt}%${dv} lần đầu${toiDa}`
  }
  if (dk.kieu === 'so_tien') {
    return `Giảm ${formatVndChuoi((dk.giaTriVnd ?? KHONG).toString())}${dv} lần đầu${toiDa}`
  }
  return `Tặng${dv === '' ? ' một dịch vụ' : dv} lần đầu${toiDa}`
}

/**
 * Câu mục đích hiện NGAY cạnh nút "Nhận ưu đãi" và được ghi nguyên văn (kèm phiên bản) vào nhật ký lúc khách nhận
 * (NĐ 13/2023 — soát bảo mật GĐ3 NẶNG 2). Đổi câu chữ = tăng phiên bản.
 */
export const CAU_DONG_Y_UU_DAI_THEO_BAN = {
  // ban-cu: Mini App Live ≤ 1.1.x vẫn hiện câu này — máy chủ ghi nguyên văn câu khách đã thấy (NĐ 13/2023).
  'uu-dai-v1':
    'Khi nhận ưu đãi, tiệm lưu tên và số điện thoại Zalo của bạn để giữ ưu đãi và nhận ra bạn khi tới tiệm.',
  // Mini App 1.2.0: đổi chữ "tiệm" → "cửa hàng" = TĂNG phiên bản (không sửa câu cũ).
  'uu-dai-v2':
    'Khi nhận ưu đãi, cửa hàng lưu tên và số điện thoại Zalo của bạn để giữ ưu đãi và nhận ra bạn khi tới cửa hàng.',
} as const
export type PhienBanDongYUuDai = keyof typeof CAU_DONG_Y_UU_DAI_THEO_BAN
export const CAC_PHIEN_BAN_DONG_Y_UU_DAI = ['uu-dai-v1', 'uu-dai-v2'] as const
/** Phiên bản HIỆN HÀNH (Mini App mới gửi). Máy chủ vẫn nhận bản cũ từ Mini App cũ. */
export const PHIEN_BAN_DONG_Y_UU_DAI: PhienBanDongYUuDai = 'uu-dai-v2'
export const CAU_DONG_Y_UU_DAI = CAU_DONG_Y_UU_DAI_THEO_BAN[PHIEN_BAN_DONG_Y_UU_DAI]
